'use client';

import React from 'react';
import Link from 'next/link';
import { BookOpen, TestTube, ExternalLink } from 'lucide-react';
import { urls } from '@/lib/config/environment';

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  external?: boolean;
}

function ResourceCard({ icon, title, description, href, external = true }: ResourceCardProps) {
  return (
    <Link
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group"
    >
      <div className="h-full p-6 rounded-sm bg-slate-900/50 border border-slate-800 hover:border-blue-500/50 hover:bg-slate-900/80 transition-all duration-300 relative overflow-hidden">
        {/* Background watermark - fills entire container, centered */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none" data-watermark-id="wm-dev-resources">
          <div className="scale-[4]">{icon}</div>
        </div>
        <div className="relative z-10 space-y-3">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-sm bg-blue-600/20 text-blue-400 flex items-center justify-center border border-blue-500/20 group-hover:bg-blue-600/30 group-hover:border-blue-500/40 transition-colors">
              {icon}
            </div>
            {external && (
              <ExternalLink size={16} className="text-slate-600 group-hover:text-blue-500 transition-colors" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-white group-hover:text-blue-200 transition-colors">{title}</h3>
          <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </Link>
  );
}

export default function DeveloperResources() {
  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Developer Resources</h2>
          <p className="text-slate-400 text-sm mt-1">
            API documentation, testing tools, and SDK references for integration
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ResourceCard
          icon={<BookOpen size={20} />}
          title="API Documentation"
          description="Interactive API reference with real-time testing capabilities powered by Scalar."
          href={urls.aiEngineApiDocs}
        />

        <ResourceCard
          icon={<TestTube size={20} />}
          title="API Test Tool"
          description="Live testing environment to experiment with AI Engine endpoints and analyze responses."
          href={urls.frontend}
        />
      </div>
    </section>
  );
}
